import { useCallback, useEffect, useRef, useState } from "react";
import { NotOfferedNotice } from "@/components/app/NotOfferedNotice";
import { ProfileNudge } from "@/components/app/profile/ProfileNudge";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import UniCard from "@/components/app/UniCard";
import DeadlineTracker from "@/components/app/DeadlineTracker";
import DeadlineTimeline from "@/components/app/DeadlineTimeline";
import DeadlinesByCountry from "@/components/app/DeadlinesByCountry";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { LogIn, GitCompare, ChevronDown, Printer } from "lucide-react";
import StudentLovedBadge from "@/components/app/StudentLovedBadge";
import { useStudentLovedBulk } from "@/lib/useStudentLoved";
import PlusCTA from "@/components/app/PlusCTA";
import PlusReportCompactStrip from "@/components/app/PlusReportCompactStrip";
import DigestSubscribeCard from "@/components/app/DigestSubscribeCard";
import ShortlistBudgetSummary, { estimateShortlistTotalCost } from "@/components/app/ShortlistBudgetSummary";
import BudgetFitChip from "@/components/app/BudgetFitChip";
import ProfileCompleteness from "@/components/app/profile/ProfileCompleteness";
import ProfileMilestoneBanner from "@/components/app/profile/ProfileMilestoneBanner";
import PswChip from "@/components/app/PswChip";
import { CAODeadlineChip } from "@/components/app/CAODeadline";
import { UCASDeadlineChip } from "@/components/app/UCASDeadlineChip";
import { engineKeyFromCountry, HOME_FEE_FALLBACK_USD } from "@/lib/countries";
import { PTDeadlineChip } from "@/components/app/PTDeadlineChip";
import CurriculumAdmitBadge from "@/components/app/CurriculumAdmitBadge";
import ShortlistRankingBadge from "@/components/app/ShortlistRankingBadge";
import { useCurrency } from "@/context/CurrencyContext";
import { readCache, writeCache } from "@/lib/useCachedFetch";
import { SHORTLIST_CACHE_PREFIX, useShortlistWriter } from "@/lib/useShortlistWriter";
import { pickAcademicSignals } from "@/lib/academic-signals";
import NssTrendSparkline from "@/components/app/NssTrendSparkline";
import StudentExperienceTrendSparkline from "@/components/app/StudentExperienceTrendSparkline";
import ApplyReadinessCard from "@/components/ApplyReadinessCard";
import ConfirmPillarPanel from "@/components/ConfirmPillarPanel";
import RealisationsPromptCard from "@/components/app/RealisationsPromptCard";
import RealisationObservations, { isOpenQuestion } from "@/components/app/RealisationObservations";
import RealisationsPatternCard from "@/components/app/RealisationsPatternCard";
import RealisationsTradeoffCard from "@/components/app/RealisationsTradeoffCard";
import ShortlistRealisationSignal from "@/components/app/ShortlistRealisationSignal";
import DecisionPicture from "@/components/app/DecisionPicture";
import CompareDecisions from "@/components/app/CompareDecisions";
import { courseTuitionUsd } from "@/lib/courseTuition";

// Any sessionStorage key that starts with this prefix is invalidated when the
// user pins/unpins — so revisits show fresh state. The prefix is defined in
// `useShortlistWriter` (single source of truth for the shortlist cache key).

const FIT_KEYS = ["prestige", "affordability", "career", "campus", "city", "sport", "diversity"];


const COUNTRY_FLAGS = {
  UK: "🇬🇧", US: "🇺🇸", Germany: "🇩🇪", France: "🇫🇷", Australia: "🇦🇺",
  Canada: "🇨🇦", Netherlands: "🇳🇱", Ireland: "🇮🇪",
};


/**
 * Renders the saved shortlist grouped into country sections — a compact
 * side-by-side comparison that surfaces cost + duration + PSW alongside
 * each pinned course. Activated by the header "Compare across N countries"
 * chip when the shortlist spans ≥2 countries.
 */
function ShortlistByCountry({ items, satisfactionMap, budget, format, feeStatus, realisationEffects, realisationHistories, decisionPictures, onDecisionPictureChanged }) {
  const groups = items.reduce((acc, it) => {
    const country = it.university?.country || "Other";
    (acc[country] = acc[country] || []).push(it);
    return acc;
  }, {});
  const countries = Object.keys(groups).sort();
  // Distinct uni_ids across every row in this shortlist — passed
  // down to <CurriculumAdmitBadge> so it fetches all bands in one
  // bulk request instead of N.
  const allShortlistUniIds = Array.from(
    new Set(items.map((it) => it.degree?.university_id || it.university_id).filter(Boolean))
  );
  return (
    <div className="mt-4 space-y-6" data-testid="shortlist-grouped">
      {countries.map((country) => {
        const rows = groups[country];
        return (
          <div key={country} className="border border-stone-200 rounded-lg bg-white p-5"
               data-testid={`shortlist-country-${country}`}>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl" aria-hidden="true">{COUNTRY_FLAGS[country] || "🌍"}</span>
              <div className="font-editorial text-xl text-stone-900">{country}</div>
              <div className="ml-auto label-uppercase !text-[10px] text-stone-500">
                {rows.length} pinned
              </div>
            </div>
            <div className="mt-4 divide-y divide-stone-100">
              {rows.map((it) => {
                const d = it.degree;
                const cost = courseTuitionUsd(d);
                const psw = it.university?.graduate_visa_years;
                return (
                  <div
                    key={it.degree_id || it.university_id}
                    className="py-3"
                    data-testid={`shortlist-row-${it.degree_id || it.university_id}`}
                  >
                    <div className="grid grid-cols-[1fr_auto_auto_auto] gap-4 items-center">
                      <div className="min-w-0">
                      <Link
                        to={d ? `/degree/${d.id}` : `/uni/${it.university_id}`}
                        className="text-sm font-medium text-stone-900 hover:text-[#4C1D95] block truncate"
                      >
                        {d?.official_title || it.university?.name}
                      </Link>
                      <div className="mt-0.5 text-[10px] font-underground-sc tracking-wider text-stone-500 truncate">
                        {it.university?.name}
                        {d && ` · ${d.qualification} · ${d.duration_years}Y`}
                      </div>
                      {budget > 0 && d && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          <PswChip
                            years={d?.international?.psw_eligible_years}
                            size="sm"
                          />
                          <BudgetFitChip
                            totalCost={estimateShortlistTotalCost(it, feeStatus)}
                            budget={budget}
                            format={format}
                            size="sm"
                          />
                        </div>
                      )}
                      <div className="hidden sm:block">
                        <ShortlistRealisationSignal
                          effect={realisationEffects[it.university_id]}
                          history={realisationHistories[it.university_id]}
                          testId={`shortlist-realisation-country-${it.degree_id || it.university_id}`}
                        />
                        <DecisionPicture
                          picture={decisionPictures[it.degree_id || it.university_id]}
                          onChanged={onDecisionPictureChanged}
                          testId={`decision-picture-${it.degree_id || it.university_id}`}
                        />
                      </div>
                      {/* Ireland CAO chip — Irish courses route
                          through the CAO national platform with a
                          fixed 1 Feb Round-1 close. Surfacing it
                          here mirrors the UCAS deadline visibility
                          UK rows already enjoy. */}
                      {d && (
                        <div className="mt-1 flex flex-wrap gap-1 items-center">
                          <CAODeadlineChip degree={d} testid={`shortlist-cao-chip-${d.id}`} />
                          <UCASDeadlineChip degree={d} testid={`shortlist-ucas-chip-${d.id}`} />
                          <PTDeadlineChip degree={d} testid={`shortlist-pt-chip-${d.id}`} />
                          <CurriculumAdmitBadge
                            universityId={d.university_id}
                            uniIdsInPage={allShortlistUniIds}
                          />
                          <ShortlistRankingBadge
                            uniId={d.university_id}
                            testid={`shortlist-rank-badge-${d.id}`}
                          />
                          {country === "United Kingdom" && (
                            <NssTrendSparkline uniId={d.university_id} />
                          )}
                          {country === "Australia" && (
                            <StudentExperienceTrendSparkline uniId={d.university_id} country="Australia" />
                          )}
                        </div>
                      )}
                      {/* Uni-only shortlist rows (no specific degree) still
                          benefit from the ranking badge — shows the strongest
                          leaderboard position across all subjects at that uni. */}
                      {!d && it.university_id && (
                        <div className="mt-1">
                          <ShortlistRankingBadge
                            uniId={it.university_id}
                            testid={`shortlist-rank-badge-uni-${it.university_id}`}
                          />
                          {country === "United Kingdom" && (
                            <NssTrendSparkline uniId={it.university_id} />
                          )}
                          {country === "Australia" && (
                            <StudentExperienceTrendSparkline uniId={it.university_id} country="Australia" />
                          )}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="label-uppercase !text-[9px] text-stone-500">Cost/yr</div>
                      <div className="text-sm font-mono text-stone-900">
                        {typeof cost === "number" ? `$${(cost / 1000).toFixed(0)}k` : "—"}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="label-uppercase !text-[9px] text-stone-500">PSW</div>
                      <div className="text-sm font-mono text-stone-900">{psw ? `${psw}y` : "—"}</div>
                    </div>
                      <div className="text-right">
                        <div className="label-uppercase !text-[9px] text-stone-500">Duration</div>
                        <div className="text-sm font-mono text-stone-900">{d?.duration_years ? `${d.duration_years}y` : "—"}</div>
                      </div>
                    </div>
                    <div className="sm:hidden">
                      <ShortlistRealisationSignal
                        effect={realisationEffects[it.university_id]}
                        history={realisationHistories[it.university_id]}
                        testId={`shortlist-realisation-country-mobile-${it.degree_id || it.university_id}`}
                      />
                      <DecisionPicture
                        picture={decisionPictures[it.degree_id || it.university_id]}
                        onChanged={onDecisionPictureChanged}
                        testId={`decision-picture-mobile-${it.degree_id || it.university_id}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}


export default function Shortlist() {
  const { user, loading, login } = useAuth();
  const { removeFromShortlist } = useShortlistWriter();
  const [items, setItems] = useState([]);
  const [groupByCountry, setGroupByCountry] = useState(false);
  // Which shortlist rows are currently open (their full detail card
  // revealed). Everything is COLLAPSED BY DEFAULT — the saved list can
  // grow to 20+ items so we lead with a scannable single-line index and
  // let the user open individual courses on demand.
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [weights, setWeights] = useState({
    prestige: 5, affordability: 5, career: 8, campus: 5, city: 5, sport: 3, diversity: 5,
  });
  const [fit, setFit] = useState([]);
  const [realisationsPrompt, setRealisationsPrompt] = useState(null);
  const [observationsLoaded, setObservationsLoaded] = useState(null);
  const [questionAnswered, setQuestionAnswered] = useState(false);
  const [promptAck, setPromptAck] = useState("");
  const promptAckRef = useRef(null);
  // One question per visit across widgets: an open observation goes first, a same-concept prompt is never shown, and
  // nothing new is chained in once the student has answered.
  const shownPrompt = realisationsPrompt && observationsLoaded && !questionAnswered && !observationsLoaded.hasOpen
    && !(realisationsPrompt.concept_ref && observationsLoaded.concepts.includes(realisationsPrompt.concept_ref))
    ? realisationsPrompt : null;
  const promptAnswered = (message) => {
    setQuestionAnswered(true);
    setRealisationsPrompt(null);
    setObservationsNonce((n) => n + 1);  // the answer now appears in "Your previous answers"
    if (message) {
      setPromptAck(message);
      requestAnimationFrame(() => promptAckRef.current?.focus());
    }
  };
  const [promptNonce, setPromptNonce] = useState(0);
  const [realisationEffects, setRealisationEffects] = useState({});
  const [realisationHistories, setRealisationHistories] = useState({});
  const [decisionPictures, setDecisionPictures] = useState({});
  const [observationsNonce, setObservationsNonce] = useState(0);
  const [compareMode, setCompareMode] = useState(false);
  const [compareKeys, setCompareKeys] = useState([]);
  // Envelope shared by the summary widget + per-row `BudgetFitChip`s. The
  // widget owns the slider state; parent stores a mirror so `UniCard` /
  // `ShortlistByCountry` rows can decorate themselves.
  const [budget, setBudget] = useState(0);
  const { format } = useCurrency();
  const satisfactionMap = useStudentLovedBulk(
    items.map((it) => it.degree_id).filter(Boolean)
  );

  // degree_id → { checked_count, total_items, next_item_label }
  // Hydrated from /api/applications. Used to swap the "Prep application"
  // button for a "Resume 62% · Next: Personal statement" button when
  // the student has started a per-degree checklist.
  const [appProgress, setAppProgress] = useState({});

  const cacheKey = user ? `${SHORTLIST_CACHE_PREFIX}${user.user_id || user.email}` : null;

  const load = async ({ force = false } = {}) => {
    if (!user) return;
    // Hydrate instantly from cache when available. The compare page and any
    // pin action bust `wua:shortlist:*` so a stale list can only appear across
    // sessionStorage isolation boundaries (i.e. a different tab).
    if (!force && cacheKey) {
      const cached = readCache(cacheKey);
      if (cached) { setItems(cached); return; }
    }
    try {
      const { data } = await api.get("/shortlist");
      const next = data.items || [];
      setItems(next);
      if (cacheKey) writeCache(cacheKey, next);
    } catch {}
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);


  const loadRealisationEffects = useCallback(async () => {
    if (!user) {
      setRealisationEffects({});
      return;
    }
    try {
      const { data } = await api.get("/realisations/shortlist-effects");
      const map = {};
      for (const effect of data?.effects || []) {
        if (effect?.university_id) map[effect.university_id] = effect;
      }
      setRealisationEffects(map);
    } catch {
      setRealisationEffects({});
    }
  }, [user]);

  const loadRealisationHistories = useCallback(async () => {
    if (!user) {
      setRealisationHistories({});
      return;
    }
    try {
      const { data } = await api.get("/realisations/shortlist-decision-history");
      const map = {};
      for (const history of data?.histories || []) {
        if (history?.university_id) map[history.university_id] = history;
      }
      setRealisationHistories(map);
    } catch {
      setRealisationHistories({});
    }
  }, [user]);

  const loadDecisionPictures = useCallback(async () => {
    if (!user) {
      setDecisionPictures({});
      return;
    }
    try {
      const { data } = await api.get("/realisations/decision-picture");
      const map = {};
      for (const o of data?.options || []) {
        if (o?.option_key) map[o.option_key] = o;
      }
      setDecisionPictures(map);
    } catch {
      setDecisionPictures({});
    }
  }, [user]);

  const onDecisionPictureAnswered = useCallback(() => {
    loadDecisionPictures();
    loadRealisationEffects();
    setObservationsNonce((n) => n + 1);
  }, [loadDecisionPictures, loadRealisationEffects]);

  const optionKeys = items.map((it) => it.degree_id || it.university_id).filter(Boolean);
  const optionKeySig = optionKeys.join("|");
  useEffect(() => {
    const current = new Set(optionKeySig.split("|"));
    setCompareKeys((prev) => (prev.every((k) => current.has(k)) ? prev : prev.filter((k) => current.has(k))));
  }, [optionKeySig]);
  const toggleCompare = (k) =>
    setCompareKeys((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : prev.length >= 2 ? prev : [...prev, k]));
  const compareChoices = compareKeys
    .map((k) => {
      const it = items.find((i) => (i.degree_id || i.university_id) === k);
      if (!it) return null;
      const place = [decisionPictures[k]?.city_name || it.university?.city, it.university?.country].filter(Boolean).join(", ");
      return {
        key: k,
        title: [it.degree?.official_title, it.university?.name].filter(Boolean).join(" · ") || "Untitled",
        place,
        picture: decisionPictures[k],
      };
    })
    .filter(Boolean);

  const shortlistUniversityKey = items
    .map((it) => it.university_id)
    .filter(Boolean)
    .sort()
    .join("|");

  useEffect(() => {
    if (!shortlistUniversityKey) {
      setRealisationEffects({});
      setRealisationHistories({});
      setDecisionPictures({});
      return;
    }
    loadRealisationEffects();
    loadRealisationHistories();
    loadDecisionPictures();
  }, [loadRealisationEffects, loadRealisationHistories, loadDecisionPictures, shortlistUniversityKey]);


  // Realisations Stage 02: observe first. Only ask the first reflection
  // question once the shortlist has reached three items. Eligibility is
  // decided server-side; this page only renders the prompt it is given.
  useEffect(() => {
    if (!user) {
      setRealisationsPrompt(null);
      return;
    }
    let cancelled = false;
    api.get("/realisations/prompts/current")
      .then(({ data }) => {
        if (!cancelled) setRealisationsPrompt(data?.prompt || null);
      })
      .catch(() => {
        if (!cancelled) setRealisationsPrompt(null);
      });
    return () => { cancelled = true; };
  }, [user, items.length, promptNonce]);

  // Fetch per-degree application progress once the shortlist has loaded.
  // `/applications` hydrates lazily on the server (one row per
  // shortlisted degree, auto-derived from profile), so this call is
  // trivially cheap.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/applications");
        if (cancelled) return;
        const map = {};
        for (const a of data.applications || []) {
          map[a.degree_id] = {
            checked_count:    a.checked_count || 0,
            total_items:      a.total_items   || 0,
            next_item_label:  a.next_item_label || null,
          };
        }
        setAppProgress(map);
      } catch {}
    })();
    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, [user, items.length]);

  // Read fee-status engine output once so per-row BudgetFitChip
  // can price home courses using the correct domestic rate — matches
  // the headline dashboard. Falls back silently when the user hasn't
  // signed in or hasn't yet set their passports.
  const [feeStatus, setFeeStatus] = useState(null);
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const load = () => api.get("/fee-status/me")
      .then(r => { if (!cancelled) setFeeStatus(r.data?.by_country || null); })
      .catch(() => {});
    load();
    const onProfileSaved = () => load();
    window.addEventListener("wua:profile-updated", onProfileSaved);
    return () => {
      cancelled = true;
      window.removeEventListener("wua:profile-updated", onProfileSaved);
    };
    /* eslint-disable-next-line */
  }, [user]);

  // Re-rank instantly after a profile save (e.g. from ProfileCompleteness
  // widget or /profile page). Busts the sessionStorage cache and refetches
  // /shortlist — the moment the user answers "career_direction" or adds a
  // dream_employer, the list visibly re-orders. That's the value-loop.
  useEffect(() => {
    const onProfileUpdated = () => load({ force: true });
    window.addEventListener("wua:profile-updated", onProfileUpdated);
    return () => window.removeEventListener("wua:profile-updated", onProfileUpdated);
    /* eslint-disable-next-line */
  }, [user]);

  const remove = async (uni_id, degree_id) => {
    try {
      await removeFromShortlist({ university_id: uni_id, degree_id });
      toast.success("Removed from shortlist");
      setFit([]);
      await load({ force: true });
      await loadRealisationEffects();
      loadDecisionPictures();
    } catch { toast.error("Could not remove"); }
  };

  const computeFit = async () => {
    // Only score universities that are actually in the user's shortlist —
    // otherwise the "Best matches" panel returned random top-of-catalogue
    // schools the applicant hadn't saved, which is confusing.
    const uniIds = Array.from(new Set(items.map((it) => it.university_id).filter(Boolean)));
    if (uniIds.length === 0) {
      toast.info("Save at least one university first, then we can re-rank them by your weighting.");
      setFit([]);
      return;
    }
    try {
      const { data } = await api.post("/fit/score", { weights, university_ids: uniIds });
      setFit(data.slice(0, 8));
    } catch { toast.error("Could not compute fit"); }
  };

  // Auto-scroll to a #hash on mount so deep links from the persona
  // card land the student right at the deadline block, not the top
  // of the page. Guard on `items.length` so we scroll only after the
  // section has actually rendered.
  useEffect(() => {
    if (typeof window === "undefined" || loading) return;
    const hash = window.location.hash;
    if (!hash) return;
    // requestAnimationFrame gives React a beat to paint the target.
    requestAnimationFrame(() => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [loading, items.length]);

  // First-load flash suppression — the fetch typically completes in <500ms
  // and a top-left "Loading…" placeholder is more jarring than a blank frame.
  if (loading) return null;

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="label-uppercase">Sign in required</div>
        <h1 className="font-editorial text-4xl mt-3">Save universities, notes and deadlines.</h1>
        <p className="mt-3 text-stone-600">Sign in once — your shortlist syncs to every device.</p>
        <Button data-testid="shortlist-login-btn" onClick={login} className="mt-8 bg-[#002147] hover:bg-[#001A38] text-white">
          <LogIn className="h-4 w-4 mr-1.5" /> Sign in
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="label-uppercase">Your shortlist</div>
          <h1 className="font-editorial text-4xl sm:text-5xl mt-2">Hello, {user.name?.split(" ")[0]}.</h1>
        </div>
        {items.length > 0 && (
          <Link
            to="/shortlist/parent-report"
            data-testid="parent-report-cta"
            className="inline-flex items-center gap-1.5 rounded-md border border-stone-300 bg-white hover:bg-stone-50 text-stone-900 text-sm px-4 py-2 transition-colors"
            title="Turn this shortlist into a one-click print-ready report to share with parents"
          >
            <Printer className="h-4 w-4" />
            Print parent-ready report
          </Link>
        )}
      </div>

      {/* Progressive profile — sharpens the match with each answer.
          Auto-hides at 100% or when dismissed for 14 days. */}
      <div className="mt-8">
        <ProfileMilestoneBanner />
        <ProfileCompleteness />
      </div>

      {/* Apply readiness — surfaces the same "N of 4 courses" gate
          used everywhere else. When the user has ≥4 courses saved,
          the confirm panel below unlocks the APPLY pillar. */}
      <ApplyReadinessCard
        count={items.length}
        className="mt-6 mb-5 sm:mb-6"
        testId="shortlist-readiness-card"
      />

      <ProfileNudge surface="shortlist" when={items.length >= 3} className="mb-6"
        priorities={["goals", "preferences", "budget"]}
        lead={`You've saved ${items.length} courses. To compare them on what matters to you,`} />

      <RealisationObservations
        user={user}
        shortlistCount={items.length}
        refreshKey={observationsNonce}
        focus
        suppressActive={Boolean(shownPrompt) || questionAnswered}
        onLoaded={(list) => setObservationsLoaded({
          concepts: list.map((o) => o.concept_ref).filter(Boolean),
          hasOpen: list.some(isOpenQuestion),
        })}
        onReacted={(data) => {
          setQuestionAnswered(true);
          if (data?.candidate_suggested) setPromptNonce((n) => n + 1);
          loadRealisationEffects();
          loadRealisationHistories();
          loadDecisionPictures();
        }}
      />


      <div ref={promptAckRef} tabIndex={-1} role="status" aria-live="polite" data-testid="realisations-pattern-ack"
        className={promptAck
          ? "mb-6 border-l-2 border-emerald-600 pl-3 text-sm text-emerald-800 focus:outline-none motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-300"
          : "sr-only"}>
        {promptAck && (
          <>
            <span className="font-semibold text-stone-900">A pattern is emerging · </span>{promptAck}{" "}
            <Link to="/realisations" className="font-medium text-[#4C1D95] underline underline-offset-2"
              data-testid="realisations-pattern-ack-link">See My Realisations</Link>
          </>
        )}
      </div>

      {shownPrompt?.kind === "pattern_confirmation" ? (
        <RealisationsPatternCard
          prompt={shownPrompt}
          onDone={promptAnswered}
        />
      ) : shownPrompt?.kind === "tradeoff_confirmation" ? (
        <RealisationsTradeoffCard
          prompt={shownPrompt}
          onDone={async () => {
            promptAnswered();
            await loadRealisationEffects();
            loadDecisionPictures();
          }}
        />
      ) : shownPrompt ? (
        <RealisationsPromptCard
          prompt={shownPrompt}
          onDone={() => promptAnswered()}
        />
      ) : null}

      {/* Confirm-Apply gate — the explicit action that turns the
          APPLY chip green on the tube map. Uses the shortlisted
          COURSES (degrees) directly so what the student confirms
          matches the counter above. The GET /shortlist response
          nests uni + degree docs under `.university` and `.degree`,
          NOT flat `_name` fields, so we pull the display name from
          there. */}
      {items.length > 0 && (
        <div className="mt-4">
          <ConfirmPillarPanel
            pillar="apply"
            choices={items.map((it) => {
              const uniName = it.university?.name || it.university_name || it.university_id;
              const degName = it.degree?.official_title || it.degree_name;
              return {
                slug:     it.degree_id || it.university_id,
                name:     degName ? `${uniName} — ${degName}` : uniName,
                uni_slug: it.university_id,
                uni_name: uniName,
                country:  it.university?.country || it.country,
              };
            })}
            min={4}
            cap={items.length}
            renderItem={(c) => c.name || c.slug}
            nextTo="/journey"
            nextLabel="See my full journey"
            confirmLabel="Confirm Shortlist Choices"
            itemNoun="course"
            testId="shortlist-apply-confirm"
            stayOnPage
          />
          <div className="mt-2 flex items-center gap-3 text-[12px]">
            <Link
              to="/admission"
              data-testid="shortlist-back-to-prove"
              className="text-stone-500 hover:text-stone-800 inline-flex items-center gap-1"
            >
              <span aria-hidden="true">←</span> Back to Prove
            </Link>
            <span className="text-stone-300">·</span>
            <span className="text-stone-500">
              Nothing gets confirmed until you tap the button above.
            </span>
          </div>
        </div>
      )}

      {/* Live budget dashboard — turns the passive wishlist into a
          stress-test: total 3-year cost, fits/over ratio, cheapest vs
          priciest. Feeds the shared `budget` state used by every row's
          BudgetFitChip below. */}
      <ShortlistBudgetSummary items={items} onBudget={setBudget} />

      {/* Compare CTA — jumps into /compare with up to 6 saved degrees
          pre-filled. Compare's cap was raised from 3 → 6 in iter56. */}
      {(() => {
        const savedDegreeIds = items.map((i) => i.degree_id).filter(Boolean);
        if (savedDegreeIds.length < 2) return null;
        const first6 = savedDegreeIds.slice(0, 6);
        const params = new URLSearchParams({ tab: "degrees" });
        first6.forEach((id, i) => params.set(`d${i + 1}`, id));
        const more = savedDegreeIds.length - first6.length;
        return (
          <div
            data-testid="compare-shortlist-cta"
            className="mt-6 rounded-lg border border-stone-300 bg-[#FDFBF7] p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          >
            <div>
              <div className="label-uppercase text-stone-500">One-click side-by-side</div>
              <div className="font-editorial text-xl mt-1">
                Compare {first6.length === 1 ? "your saved course" : `${first6.length} of your saved courses`} side by side
              </div>
              {more > 0 && (
                <p className="text-xs text-stone-500 mt-1">
                  Comparing the first 6 · {more} more saved course{more === 1 ? "" : "s"} — swap them in from the Compare page picker.
                </p>
              )}
            </div>
            <Link
              to={`/compare?${params.toString()}`}
              data-testid="compare-shortlist-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-[#002147] hover:bg-[#001A38] text-white text-sm px-4 py-2.5 transition-colors shrink-0"
            >
              <GitCompare className="h-4 w-4" />
              Compare all my saved courses
            </Link>
          </div>
        );
      })()}

      {/* $49 pre-WUA+ Personalised Report — surfaces the moment the
          user has built a shortlist worth reporting on (≥3 items).
          Cheaper anchor above the WUA+ counsellor CTA below, so the
          Free → $49 report → WUA+ 1:1 escalation reads cleanly. */}
      {items.length >= 3 && (
        <div className="mt-6">
          <PlusReportCompactStrip
            testId="shortlist-plus-report-strip"
            hook={`You've saved ${items.length} course${items.length === 1 ? "" : "s"}. A WUA Personalised Report stress-tests portfolio balance, financial fit and visa order — in 60 seconds.`}
          />
        </div>
      )}

      {/* Jump-off to WUA+ — natural upgrade moment on the Shortlist page */}
      <div className="mt-8">
        <PlusCTA
          variant="inline"
          testId="shortlist-plus-cta"
          headline="Want a counsellor on your shortlist?"
          body="You've built a great list. A WUA+ counsellor can pressure-test each choice against your grades, budget and career goal — and help you rank them."
          primaryLabel="See WUA+"
        />
      </div>

      {/* Personal fit weighting */}
      <Card className="mt-8 p-6 border-stone-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-uppercase">Personal-fit weighting</div>
            <p className="text-sm text-stone-600 mt-1">
              Slide what matters to you. We'll re-rank the universities <span className="font-semibold">in your shortlist</span> and explain why each one fits.
            </p>
          </div>
          <Button onClick={computeFit} data-testid="fit-recompute-btn" className="bg-[#002147] hover:bg-[#001A38] text-white rounded-md">
            Recompute fit
          </Button>
        </div>
        <div className="mt-6 grid md:grid-cols-4 lg:grid-cols-7 gap-6">
          {FIT_KEYS.map((k) => (
            <div key={k}>
              <div className="label-uppercase !text-[10px] whitespace-nowrap">{k} — {weights[k]}</div>
              <Slider min={0} max={10} step={1}
                      value={[weights[k]]}
                      data-testid={`fit-slider-${k}`}
                      onValueChange={(v) => setWeights((w) => ({ ...w, [k]: v[0] }))}
                      className="mt-3" />
            </div>
          ))}
        </div>
        {fit.length > 0 && (
          <div className="mt-8" data-testid="fit-ranked-shortlist">
            <div className="label-uppercase mb-3">Your shortlist, re-ranked</div>
            <div className="grid md:grid-cols-2 gap-3">
              {fit.map((f) => (
                <div key={f.university_id} data-testid={`fit-result-${f.university_id}`}
                     className="flex items-center justify-between border border-stone-200 rounded-md p-3 bg-white">
                  <div>
                    <div className="font-editorial text-lg">{f.university_name}</div>
                    <div className="text-xs text-stone-500 mt-0.5">{f.why}</div>
                  </div>
                  <div className="font-chip text-2xl font-semibold">{f.score}<span className="text-stone-400 text-sm">%</span></div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Family-scan strip — earliest deadline grouped by country
          so parents see "🇬🇧 UK · 25 Jan" at a glance. */}
      <DeadlinesByCountry items={items} />

      {/* Guided visual timeline (P0) — one-glance view across every dated
          event on the shortlist. Sits above the per-degree tracker. */}
      <DeadlineTimeline items={items} />

      {/* Application-deadline tracker (P1) — reads each item's degree.application block */}
      <div id="deadlines">
        <DeadlineTracker items={items} />
      </div>

      {/* Weekly-digest opt-in — surfaces immediately below the deadline
          tracker so it lives in the same visual band as the family's
          "keep an eye on things" tooling. */}
      <div className="mt-6" data-testid="shortlist-digest-slot">
        <DigestSubscribeCard />
      </div>

      {/* Saved */}
      <div className="mt-10">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div className="label-uppercase">Saved universities &amp; degrees</div>
          {user && items.length >= 2 && (
            <button
              type="button"
              onClick={() => {
                setCompareMode((v) => !v);
                setGroupByCountry(false);
              }}
              data-testid="shortlist-compare-toggle"
              aria-pressed={compareMode}
              className={`ml-auto inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-underground-sc tracking-wider transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4C1D95] focus-visible:ring-offset-2 ${
                compareMode
                  ? "border-[#4C1D95] bg-[#4C1D95] text-white"
                  : "border-stone-300 bg-white text-stone-700 hover:border-[#4C1D95]"
              }`}
            >
              {compareMode ? "Done comparing" : "Compare two choices"}
            </button>
          )}
          {(() => {
            const countries = Array.from(new Set(items.map((i) => i.university?.country).filter(Boolean)));
            if (countries.length < 2) return null;
            return (
              <button
                type="button"
                onClick={() => {
                  setGroupByCountry((v) => !v);
                  setCompareMode(false);
                }}
                data-testid="shortlist-group-toggle"
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-underground-sc tracking-wider transition-colors ${
                  groupByCountry
                    ? "border-[#4C1D95] bg-[#4C1D95] text-white"
                    : "border-stone-300 bg-white text-stone-700 hover:border-[#4C1D95]"
                }`}
              >
                Compare across {countries.length} countries {groupByCountry ? "✓" : "→"}
              </button>
            );
          })()}
        </div>
        {compareMode && items.length > 0 && (
          <CompareDecisions
            choices={compareChoices}
            onRemove={(k) => setCompareKeys((prev) => prev.filter((x) => x !== k))}
            onChanged={onDecisionPictureAnswered}
            nonce={observationsNonce}
          />
        )}
        {items.length === 0 ? (
          <div className="mt-4 p-8 border border-dashed border-stone-300 rounded-lg text-stone-500 text-sm">
            Nothing shortlisted yet. Browse the <a href="/explore" className="underline">explorer</a> and press "Shortlist this degree" on any degree.
          </div>
        ) : groupByCountry && !compareMode ? (
          <ShortlistByCountry items={items} satisfactionMap={satisfactionMap} budget={budget} format={format} feeStatus={feeStatus} realisationEffects={realisationEffects} realisationHistories={realisationHistories} decisionPictures={decisionPictures} onDecisionPictureChanged={onDecisionPictureAnswered} />
        ) : (
          // Collapsed-by-default list — each row is a one-liner header
          // (title + uni + expand chevron + remove) and clicking the
          // header opens the full course card below it. Keeps the page
          // scannable when the applicant has pinned 15-25 courses.
          <ul className="mt-4 divide-y divide-stone-200 border border-stone-200 rounded-lg bg-white">
            {items.map((it) => {
              const key = it.degree_id
                ? `${it.university_id}::${it.degree_id}`
                : it.university_id;
              const isOpen = expandedIds.has(key);
              const toggle = () =>
                setExpandedIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(key)) next.delete(key);
                  else next.add(key);
                  return next;
                });
              return (
                <li key={key} data-testid={`saved-item-${it.degree_id || it.university_id}`}>
                  <div className="flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-stone-50 transition-colors sm:flex-nowrap">
                    <button
                      type="button"
                      onClick={toggle}
                      aria-expanded={isOpen}
                      data-testid={`saved-item-toggle-${it.degree_id || it.university_id}`}
                      className="flex-1 min-w-0 basis-full flex items-center gap-3 text-left sm:basis-auto"
                    >
                      <ChevronDown
                        className={`h-4 w-4 shrink-0 text-stone-500 transition-transform ${
                          isOpen ? "rotate-0" : "-rotate-90"
                        }`}
                        aria-hidden
                      />
                      <div className="min-w-0 flex-1">
                        <div className="font-editorial text-base leading-tight truncate">
                          {it.degree?.official_title || it.university?.name || "Untitled"}
                        </div>
                        <div className="mt-0.5 text-[10px] font-underground-sc tracking-wider text-stone-500 truncate">
                          {it.university?.name}
                          {it.university?.country && ` · ${it.university.country}`}
                          {it.degree && ` · ${it.degree.qualification} · ${it.degree.duration_years}Y`}
                        </div>
                        {it.career_direction_match?.matched?.length > 0 && (
                          <div
                            data-testid={`career-match-chip-${it.degree_id || it.university_id}`}
                            className="mt-1.5 inline-flex max-w-full flex-wrap items-center gap-x-1 rounded-xl border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-800"
                            title={it.career_direction_match.reason}
                          >
                            <span aria-hidden>✨</span>
                            <span className="font-medium">Matches your goal</span>
                            <span className="min-w-0 max-w-full text-emerald-700 truncate sm:max-w-[16em]">
                              — {it.career_direction_match.matched.slice(0, 3).join(", ")}
                            </span>
                          </div>
                        )}
                        <div className="hidden sm:block">
                          <ShortlistRealisationSignal
                            effect={realisationEffects[it.university_id]}
                            history={realisationHistories[it.university_id]}
                            testId={`shortlist-realisation-${it.degree_id || it.university_id}`}
                          />
                        </div>
                      </div>
                    </button>
                    {compareMode && (() => {
                      const ck = it.degree_id || it.university_id;
                      const checked = compareKeys.includes(ck);
                      const full = !checked && compareKeys.length >= 2;
                      return (
                        <label
                          className={`inline-flex shrink-0 items-center gap-1.5 text-[11px] ${full ? "text-stone-300" : "text-stone-600 cursor-pointer"}`}
                          data-testid={`compare-select-label-${ck}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={full}
                            onChange={() => toggleCompare(ck)}
                            aria-label={`Compare ${it.degree?.official_title || it.university?.name || "this choice"}`}
                            data-testid={`compare-select-${ck}`}
                            className="h-3.5 w-3.5 accent-[#4C1D95]"
                          />
                          Compare
                        </label>
                      );
                    })()}
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`remove-shortlist-${it.degree_id || it.university_id}`}
                      onClick={() => remove(it.university_id, it.degree_id)}
                    >
                      Remove
                    </Button>
                    {it.degree_id && (() => {
                      const prog = appProgress[it.degree_id];
                      const started = prog && prog.checked_count > 0 && prog.total_items > 0;
                      const pct = started
                        ? Math.round((prog.checked_count / prog.total_items) * 100)
                        : 0;
                      // 0% = no pill, just the plain Prep CTA (per UX spec).
                      if (!started) {
                        return (
                          <Link
                            to={`/applications/${it.degree_id}`}
                            data-testid={`open-application-${it.degree_id}`}
                            className="inline-flex items-center justify-center border border-stone-300 hover:border-emerald-500 hover:text-emerald-700 rounded-md text-xs px-2 py-1"
                          >
                            Prep application
                          </Link>
                        );
                      }
                      return (
                        <Link
                          to={`/applications/${it.degree_id}`}
                          data-testid={`open-application-${it.degree_id}`}
                          title={prog.next_item_label ? `Next: ${prog.next_item_label}` : undefined}
                          className="inline-flex items-center gap-2 border border-emerald-400 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 rounded-md text-xs px-2 py-1"
                        >
                          <span className="font-semibold tabular-nums" data-testid={`application-progress-pct-${it.degree_id}`}>{pct}%</span>
                          <span className="text-emerald-600" aria-hidden>·</span>
                          <span
                            className="hidden sm:inline truncate max-w-[14em]"
                            data-testid={`application-next-step-${it.degree_id}`}
                          >
                            Next: {prog.next_item_label || "Finish checklist"}
                          </span>
                          <span className="sm:hidden">Resume</span>
                        </Link>
                      );
                    })()}
                  </div>
                  {it.not_offered && (
                    <div className="px-4 pb-3">
                      <NotOfferedNotice info={it.not_offered} testId={`not-offered-${it.degree_id}`} />
                    </div>
                  )}
                  <div className="px-4 pb-3 sm:hidden">
                    <ShortlistRealisationSignal
                      effect={realisationEffects[it.university_id]}
                      history={realisationHistories[it.university_id]}
                      testId={`shortlist-realisation-mobile-${it.degree_id || it.university_id}`}
                    />
                  </div>
                  {decisionPictures[it.degree_id || it.university_id] && (
                    <div className="px-4 pb-3 min-w-0">
                      <DecisionPicture
                        picture={decisionPictures[it.degree_id || it.university_id]}
                        onChanged={onDecisionPictureAnswered}
                        testId={`decision-picture-list-${it.degree_id || it.university_id}`}
                      />
                    </div>
                  )}
                  {isOpen && (
                    <div
                      className="border-t border-stone-100 bg-stone-50/40 px-4 py-4"
                      data-testid={`saved-item-body-${it.degree_id || it.university_id}`}
                    >
                      {it.degree ? (
                        <Card className="p-5 border-stone-200 bg-white">
                          <div className="label-uppercase !text-[10px]">
                            {it.degree.qualification} · {it.degree.duration_years}Y · {it.degree.campus}
                          </div>
                          <h3 className="font-editorial text-lg mt-1 leading-tight">
                            <Link
                              to={`/degree/${it.degree.id}`}
                              className="hover:underline underline-offset-4"
                            >
                              {it.degree.official_title}
                            </Link>
                          </h3>
                          <div className="mt-1 flex items-center gap-2 flex-wrap">
                            <div className="text-xs text-stone-500">
                              <Link
                                to={`/uni/${it.university_id}`}
                                className="hover:underline"
                              >
                                {it.university?.name}
                              </Link>
                              {it.university?.country ? ` · ${it.university.country}` : ""}
                            </div>
                            <StudentLovedBadge
                              stats={satisfactionMap[it.degree.id]}
                              size="xs"
                              testid={`student-loved-shortlist-${it.degree.id}`}
                            />
                          </div>
                          {budget > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              <PswChip
                                years={it.degree?.international?.psw_eligible_years}
                                size="sm"
                              />
                              <BudgetFitChip
                                totalCost={estimateShortlistTotalCost(it, feeStatus)}
                                budget={budget}
                                format={format}
                                size="sm"
                              />
                            </div>
                          )}
                          <div className="mt-4 font-mono text-xs text-stone-500">
                            Application code ·{" "}
                            <span className="text-stone-800">
                              {it.degree.application_code || "—"}
                            </span>
                          </div>
                          {(() => {
                            // Grade signals — Advertised (cited) is the
                            // primary. When it's missing (~5,800 UK
                            // courses today), fall back to the HESA
                            // intake badged clearly as "Intake · X" so
                            // families never mistake it for the offer.
                            const sig = pickAcademicSignals(it.university, it.degree);
                            const adv = sig.advertised?.value;
                            const intk = sig.intake?.value;
                            const uniW = sig.uniWide?.value;

                            // Country-specific fallback — for non-UK/IE
                            // degrees, `pickAcademicSignals` returns
                            // all-null on purpose (A-Level context
                            // doesn't apply). Pick up the native
                            // signal instead so US/AU/NL rows still
                            // carry a grade chip.
                            const er = it.degree.entry_requirements || {};
                            const country = it.university?.country;
                            let nativeLabel = null;
                            let nativeValue = null;
                            let nativeEstimated = false;
                            if (country === "US" || country === "United States") {
                              if (er.sat?.typical != null) {
                                nativeLabel = "SAT typical";
                                nativeValue = er.sat.typical;
                              } else if (er.act?.typical != null) {
                                nativeLabel = "ACT typical";
                                nativeValue = er.act.typical;
                              } else if (er.gpa?.typical != null) {
                                nativeLabel = "GPA typical";
                                nativeValue = er.gpa.typical;
                                nativeEstimated = er.gpa.estimated === true;
                              }
                            }

                            if (!adv && !intk && !uniW && !sig.ucasTariff && !nativeValue) return null;
                            return (
                              <div
                                className="mt-2 font-mono text-xs text-stone-500"
                                data-testid={`shortlist-grade-${it.degree.id}`}
                              >
                                {adv ? (
                                  <>
                                    Advertised ·{" "}
                                    <span className="text-stone-800 font-semibold">{adv}</span>
                                    {intk && intk !== adv && (
                                      <span className="ml-2 text-stone-500">
                                        Intake · <span className="text-stone-700">{intk}</span>
                                      </span>
                                    )}
                                  </>
                                ) : intk ? (
                                  <>
                                    Intake ·{" "}
                                    <span className="text-stone-800 font-semibold">{intk}</span>
                                    <span className="ml-1 text-[10px] text-stone-400">(HESA · advertised offer not yet verified)</span>
                                  </>
                                ) : uniW ? (
                                  <>
                                    Uni-wide typical ·{" "}
                                    <span className="text-stone-800 font-semibold">{uniW}</span>
                                  </>
                                ) : sig.ucasTariff?.standard != null ? (
                                  <>
                                    UCAS tariff ·{" "}
                                    <span className="text-stone-800 font-semibold">{sig.ucasTariff.standard} pts</span>
                                  </>
                                ) : nativeValue != null ? (
                                  <>
                                    {nativeLabel} ·{" "}
                                    <span className="text-stone-800 font-semibold">{nativeValue}</span>
                                    {nativeEstimated && (
                                      <span
                                        className="ml-1.5 inline-flex items-center px-1 py-0 rounded-full text-[9px] font-semibold tracking-wider uppercase bg-amber-100 text-amber-900 border border-amber-300 align-middle"
                                        title="Estimated from institutional acceptance rate — not a published cutoff."
                                      >
                                        est.
                                      </span>
                                    )}
                                  </>
                                ) : null}
                              </div>
                            );
                          })()}
                        </Card>
                      ) : (
                        it.university && (() => {
                          // Resolve the student's fee status for this
                          // uni's country so the card can show the
                          // Home/Intl pill and the right annual total.
                          const key = engineKeyFromCountry(it.university?.country);
                          const inf = key && feeStatus ? feeStatus[key] : null;
                          const fs = inf ? {
                            status: inf.status,
                            reason: inf.reason,
                            home_fee_fallback_usd: HOME_FEE_FALLBACK_USD[key],
                          } : null;
                          return <UniCard uni={it.university} feeStatus={fs} />;
                        })()
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
